/**
 * Ambient Audio & Soundwave Beat Controller
 * When the user loads and clicks anywhere on the page,
 * the ambient audio automatically starts playing in a seamless loop with
 * smooth volume fade-in, the soundwave icon animates, and the beat circle pulses.
 *
 * Architecture: Nirajan <https://nbk.com.np>
 */

export class AmbientAudioController {
  constructor() {
    this.audio = document.getElementById('nbk-ambient-audio');
    this.toggleBtn = document.getElementById('nbk-audio-toggle');

    if (!this.audio || !this.toggleBtn) return;

    this.isPlaying = false;
    this.targetVolume = 0.1;
    this.globalInteractionHandler = null;

    // Load available audio tracks
    try {
      this.tracks = JSON.parse(this.audio.getAttribute('data-tracks') || '[]');
    } catch {
      this.tracks = [];
    }

    let savedIndex = null;
    try {
      savedIndex = window.localStorage
        ? window.localStorage.getItem('nbk_ambient_track_index')
        : null;
    } catch {
      savedIndex = null;
    }

    this.currentTrackIndex = savedIndex !== null ? parseInt(savedIndex, 10) : 0;
    if (isNaN(this.currentTrackIndex) || this.currentTrackIndex >= this.tracks.length) {
      this.currentTrackIndex = 0;
    }

    // Audio src is lazily loaded on first interaction to save 4MB initial payload
    this.init();
  }

  ensureAudioSrc() {
    if (!this.audio.src && this.tracks.length > 0) {
      this.audio.src = this.tracks[this.currentTrackIndex];
    }
  }

  init() {
    // Keep the current track looping continuously until page refresh or new session
    this.audio.loop = true;
    this.audio.volume = this.targetVolume;

    // Direct synchronization with native audio element events
    this.audio.addEventListener('play', () => this.setPlayingState(true));
    this.audio.addEventListener('playing', () => this.setPlayingState(true));
    this.audio.addEventListener('pause', () => this.setPlayingState(false));

    // Explicit toggle button click handler
    this.toggleBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggleAudio();
    });

    // Bind user interaction to start playback on demand
    this.bindGlobalClickToPlay();
  }

  bindGlobalClickToPlay() {
    this.globalInteractionHandler = (e) => {
      // If the user clicked the toggle button directly, let its own handler execute
      if (this.toggleBtn && (this.toggleBtn === e.target || this.toggleBtn.contains(e.target))) {
        return;
      }

      if (this.audio && this.audio.paused) {
        this.playAudioWithFade();
      }
    };

    // Use capture phase on document so no child elements can block the click
    document.addEventListener('click', this.globalInteractionHandler, {
      capture: true,
      once: true,
    });
    document.addEventListener('pointerdown', this.globalInteractionHandler, {
      capture: true,
      once: true,
    });
  }

  removeGlobalClickToPlay() {
    if (this.globalInteractionHandler) {
      document.removeEventListener('click', this.globalInteractionHandler, { capture: true });
      document.removeEventListener('pointerdown', this.globalInteractionHandler, { capture: true });
      this.globalInteractionHandler = null;
    }
  }

  playAudioWithFade() {
    this.ensureAudioSrc();
    this.audio.volume = 0;
    const playPromise = this.audio.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          this.setPlayingState(true);
          this.fadeVolumeIn();
          // Successfully playing, remove global listener
          this.removeGlobalClickToPlay();
        })
        .catch((err) => {
          console.warn('Audio playback could not start:', err);
        });
    }
  }

  fadeVolumeIn(duration = 1000) {
    const startTime = window.performance ? window.performance.now() : Date.now();
    const target = Math.max(0, Math.min(this.targetVolume, 1));

    const step = (now) => {
      const currentTime = now || (window.performance ? window.performance.now() : Date.now());
      const elapsed = currentTime - startTime;
      const progress = Math.max(0, Math.min(elapsed / duration, 1));
      if (this.audio) {
        this.audio.volume = Math.max(0, Math.min(progress * target, 1));
      }

      if (progress < 1 && this.isPlaying) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }

  toggleAudio() {
    if (this.audio.paused) {
      this.playAudioWithFade();
    } else {
      this.audio.pause();
      this.setPlayingState(false);
    }
  }

  setPlayingState(playing) {
    this.isPlaying = playing;

    if (playing) {
      this.toggleBtn.classList.add('is-playing');
      this.toggleBtn.classList.remove('is-paused');
      this.toggleBtn.setAttribute('aria-label', 'Pause Ambient Music');
    } else {
      this.toggleBtn.classList.remove('is-playing');
      this.toggleBtn.classList.add('is-paused');
      this.toggleBtn.setAttribute('aria-label', 'Play Ambient Music');
    }
  }
}
